import React from 'react';
import { useAtom } from 'jotai';
import { useAtomValue, useUpdateAtom } from 'jotai/utils';
import cn from 'classnames';

import Tag from 'src/components/Tag';
import Button from 'src/components/Button';
import {
  dashboardFiltersAtom,
  setFilterAtom,
  clearAllFiltersAtom,
  anyFilterActiveAtom,
  FILTER_DESCRIPTORS,
} from 'src/shared/dashboardFilters';

import { useAccessToken } from 'src/hooks/useAccessToken';
import { getThumbnailUrl } from 'src/utils/previews';

import styles from './ActiveFilters.module.scss';

type Props = {
  className?: string;
};

/**
 * Renders one pill per active filter by walking `FILTER_DESCRIPTORS`. The
 * descriptor table is the single source of truth for which filters are
 * user-visible — to add a new filter, add a descriptor entry, and a pill
 * comes for free.
 *
 * Pairs with `<Button>Clear all</Button>` when at least one filter is set.
 */
export const ActiveFilters: React.FC<Props> = ({ className }) => {
  const [state] = useAtom(dashboardFiltersAtom);
  const apply = useUpdateAtom(setFilterAtom);
  const clearAll = useUpdateAtom(clearAllFiltersAtom);
  const anyActive = useAtomValue(anyFilterActiveAtom);
  const accessToken = useAccessToken();

  if (!anyActive) return null;

  const pills = FILTER_DESCRIPTORS.flatMap((d) =>
    d.isActive(state) ? d.toPills(state, apply) : [],
  );

  return (
    <div className={cn(styles.root, className)} data-test="active-filters">
      {pills.map((p) => (
        <Tag
          key={p.id}
          text={p.label}
          onClose={p.onClear}
          imageUrl={
            p.thumbnailAssetId
              ? getThumbnailUrl({ width: 40, height: 40, id: p.thumbnailAssetId, jwt: accessToken })
              : undefined
          }
        />
      ))}
      <Button
        size="small"
        mode="flat"
        onClick={() => clearAll()}
        data-test="active-filters-clear-all"
      >
        Clear all
      </Button>
    </div>
  );
};

export default ActiveFilters;
